import { useEvent } from '../hooks/useEvent';
import constate from 'constate';
import { useEffect, useState } from 'react';

const useCookieStore = () => {
  const [localStorageVariable, setLocalStorageVariable] = useState<any>(null);
  
  const [othersCookiesOptions, setOthersCookiesOptions] = useState({
    googleAds: false,
    googleAnalytics: false,
    facebookPixel: false,
    microsoftAds: false,
  });

  const [consents, setConsents] = useState({
    ad_personalization: false,
    ad_storage: false,
    ad_user_data: false,
    analytics_storage: false
  });

  useEffect(() => {
    const data = localStorage.getItem("consentMode");
    if (data) {
      const consent = JSON.parse(data);
      setLocalStorageVariable(consent);
      setOthersCookiesOptions({
        googleAds: consent.google_ads === "granted",
        googleAnalytics: consent.google_analytics === "granted",
        facebookPixel: consent.facebook_pixel === "granted",
        microsoftAds: consent.microsoft_ads === "granted",
      });
    }
    const dataConsents = localStorage.getItem("consents");
    if (dataConsents) {
      const consentConsents = JSON.parse(dataConsents);
      setConsents({
        ad_personalization: consentConsents.ad_personalization,
        ad_storage: consentConsents.ad_storage,
        ad_user_data: consentConsents.ad_user_data,
        analytics_storage: consentConsents.analytics_storage
      });
    }
  }, []);
  
  const handleAcceptCookies = useEvent(() => {
    setConsentApply(othersCookiesOptions, consents);
  });

  const handleAcceptCookiesAll = useEvent(() => {
    setConsent({
      googleAds: true,
      googleAnalytics: true,
      facebookPixel: true,
      microsoftAds: true,
    });
    localStorage.setItem("consents", JSON.stringify({
      ad_personalization: true,
      ad_storage: true,
      ad_user_data: true,
      analytics_storage: true,
    }));
  });

  const handleAcceptCookiesOnLoad = useEvent(() => {
    const dataConsents = localStorage.getItem("consents");
    const consentConsents = JSON.parse(dataConsents ?? '{}');
    // Google Tag
    if (typeof gtag === 'function') {
      let googleAnalytics = consentConsents.analytics_storage;
      if(consentConsents.analytics_storage_second){
        googleAnalytics = consentConsents.analytics_storage_second;
      }
      const consentGtag = {
        ad_personalization: consentConsents.ad_personalization ? "granted" : "denied",
        ad_storage: consentConsents.ad_storage ? "granted" : "denied",
        ad_user_data: consentConsents.ad_user_data ? "granted" : "denied",
        analytics_storage: googleAnalytics ? "granted" : "denied",
      };
      gtag("consent", "update", consentGtag);
    } else {
      console.warn("gtag is not defined.");
    }
  });

  const handleDeclineCookies = useEvent(() => {
    setConsent({
      googleAds: false,
      googleAnalytics: false,
      facebookPixel: false,
      microsoftAds: false,
    });
    localStorage.setItem("consents", JSON.stringify({
      ad_personalization: false,
      ad_storage: false,
      ad_user_data: false,
      analytics_storage: false,
    }));
  });

  const setConsentApply = (consent: any, consents: any) => {
    const consentMode = {
      google_ads: consent.googleAds ? "granted" : "denied",
      google_analytics: consent.googleAnalytics ? "granted" : "denied",
      facebook_pixel: consent.facebookPixel ? "granted" : "denied",
      microsoft_ads: consent.microsoftAds ? "granted" : "denied",
    };

    localStorage.setItem("consentMode", JSON.stringify(consentMode));
    localStorage.setItem("consents", JSON.stringify(consents));
    setLocalStorageVariable(consentMode);
    applyConsents(consentMode, consents);
  };

  const setConsent = (consent: any) => {
    const consentMode = {
      google_ads: consent.googleAds ? "granted" : "denied",
      google_analytics: consent.googleAnalytics ? "granted" : "denied",
      facebook_pixel: consent.facebookPixel ? "granted" : "denied",
      microsoft_ads: consent.microsoftAds ? "granted" : "denied",
    };

    localStorage.setItem("consentMode", JSON.stringify(consentMode));
    setLocalStorageVariable(consentMode);
    applyConsent(consentMode);
  };

  const applyConsents = (consentMode: Record<string, string>, consents: any) => {
    // Google Tag
    if (typeof gtag === 'function') {
      const consentGtag = {
        ad_personalization: consents.ad_personalization ? "granted" : "denied",
        ad_storage: consents.ad_storage ? "granted" : "denied",
        ad_user_data: consents.ad_user_data ? "granted" : "denied",
        analytics_storage: consents.analytics_storage ? "granted" : "denied",
      };
      gtag("consent", "update", consentGtag);
    } else {
      console.warn("gtag is not defined.");
    }

    // Facebook Pixel
    if (typeof fbq === 'function') {
      fbq("consent", consentMode.facebook_pixel === "granted" ? "grant" : "revoke");
    } else {
      console.warn("fbq is not defined.");
    }

    // Microsoft Ads
    if (window.uetq) {
      window.uetq.push('consent', 'update', {
        'ad_storage': consentMode.microsoft_ads === "granted" ? "granted" : "denied"
      });
    } else {
      console.warn("Microsoft Ads uetq is not defined.");
    }
  };

  const applyConsent = (consentMode: Record<string, string>) => {
    if (typeof gtag === 'function') {
      const consentGtag = {
        ad_personalization: consentMode.google_ads,
        ad_storage: consentMode.google_ads,
        ad_user_data: consentMode.google_ads,
        analytics_storage: consentMode.google_analytics,
      };
      gtag("consent", "update", consentGtag);
    } else {
      console.warn("gtag is not defined.");
    }

    if (typeof fbq === 'function') {
      fbq("consent", consentMode.facebook_pixel === "granted" ? "grant" : "revoke");
    } else {
      console.warn("fbq is not defined.");
    }

    if (window.uetq) {
      window.uetq.push('consent', 'update', {
        'ad_storage': consentMode.microsoft_ads === "granted" ? "granted" : "denied"
      });
    } else {
      console.warn("Microsoft Ads uetq is not defined.");
    }
  };

  return { localStorageVariable, handleAcceptCookies, handleDeclineCookies, handleAcceptCookiesOnLoad, handleAcceptCookiesAll, othersCookiesOptions, setOthersCookiesOptions, consents, setConsents };
};

const [CookieStoreProvider, useCookieStoreContext] = constate(useCookieStore);

export { CookieStoreProvider, useCookieStoreContext };
